// 用量数据处理：缓存解析、聚合统计、CSV 构建
import { timestamp2string } from './utils';

export const QUOTA_PER_UNIT = 500000; // 1 美元对应的额度值

export function quotaToUSD(quota) {
  return (quota || 0) / QUOTA_PER_UNIT;
}

// 大数字紧凑格式：1,284 / 12.9K / 4.2M / 1.1B
export function formatCompact(num) {
  const n = Number(num) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (n / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e4) return (n / 1e3).toFixed(abs >= 1e5 ? 0 : 1) + 'K';
  return n.toLocaleString('en-US');
}

export function formatFull(num) {
  return (Number(num) || 0).toLocaleString('en-US');
}

export function parseOther(record) {
  try {
    if (!record.other || record.other === '') return {};
    return JSON.parse(record.other) || {};
  } catch (e) {
    return {};
  }
}

// 从日志 other 字段中提取缓存 tokens（兼容多种后端字段名）
export function getCacheTokens(record) {
  const other = parseOther(record);
  const creation = other.cache_creation || {};

  const write5m = Number(
    other.cache_creation_5m_tokens ??
    other.cache_5m_tokens ??
    creation.ephemeral_5m_input_tokens ??
    0
  ) || 0;

  const write1h = Number(
    other.cache_creation_1h_tokens ??
    other.cache_1h_tokens ??
    creation.ephemeral_1h_input_tokens ??
    0
  ) || 0;

  const writeTotal = Number(
    other.cache_creation_input_tokens ??
    other.cache_creation_tokens ??
    other.cached_write_tokens ??
    other.cache_write_tokens ??
    0
  ) || 0;

  const read = Number(
    other.cache_read_input_tokens ??
    other.cached_tokens ??
    other.cache_read_tokens ??
    other.cached_read_tokens ??
    other.cache_tokens ??
    0
  ) || 0;

  // 没有细分 5m/1h 时，剩余写入量归入"其他写入"
  const writeOther = Math.max(writeTotal - write5m - write1h, 0);
  const writeAll = Math.max(writeTotal, write5m + write1h);

  return { write5m, write1h, writeOther, writeTotal, writeAll, read };
}

// 是否为消费类日志（type 0/2）
export function isUsageLog(log) {
  return log.type === 0 || log.type === 2;
}

function emptyTotals() {
  return {
    requests: 0,
    promptTokens: 0,
    completionTokens: 0,
    totalTokens: 0,
    inputTokens: 0,
    cacheRead: 0,
    cacheWrite: 0,
    cacheWrite5m: 0,
    cacheWrite1h: 0,
    quota: 0,
    useTimeSum: 0,
    streamCount: 0,
  };
}

// 判断该条日志的用量语义是否为 Anthropic 式：缓存读/写在 prompt_tokens 之外单独上报
// （提示可能是 1 而缓存几十万）；OpenAI/Gemini 式的缓存命中已包含在 prompt_tokens 内。
// 优先用后端在 other 里标记的 usage_semantic / claude 字段，旧日志缺字段时退回启发式。
function isAnthropicUsage(log, cache) {
  const other = parseOther(log);
  if (other.usage_semantic) return other.usage_semantic === 'anthropic';
  if (other.claude === true) return true;
  const model = (log.model_name || '').toLowerCase();
  return model.includes('claude') || cache.writeAll > 0;
}

function accumulate(acc, log) {
  const cache = getCacheTokens(log);
  const prompt = Number(log.prompt_tokens) || 0;
  const completion = Number(log.completion_tokens) || 0;
  // Anthropic 语义下缓存不含在提示里，总量需另行累加
  const cacheExtra = isAnthropicUsage(log, cache) ? cache.read + cache.writeAll : 0;
  acc.requests += 1;
  acc.promptTokens += prompt;
  acc.completionTokens += completion;
  acc.inputTokens += prompt + cacheExtra;
  acc.totalTokens += prompt + completion + cacheExtra;
  acc.cacheRead += cache.read;
  acc.cacheWrite += cache.writeAll;
  acc.cacheWrite5m += cache.write5m;
  acc.cacheWrite1h += cache.write1h;
  acc.quota += Number(log.quota) || 0;
  acc.useTimeSum += Number(log.use_time) || 0;
  if (log.is_stream) acc.streamCount += 1;
  return acc;
}

// 总体统计
export function summarize(logs) {
  const totals = logs.reduce(accumulate, emptyTotals());
  totals.usd = quotaToUSD(totals.quota);
  totals.avgUseTime = totals.requests > 0 ? totals.useTimeSum / totals.requests : 0;
  totals.streamRate = totals.requests > 0 ? totals.streamCount / totals.requests : 0;
  totals.cacheHitRate = totals.inputTokens > 0 ? totals.cacheRead / totals.inputTokens : 0;
  return totals;
}

function dayKey(timestamp) {
  const d = new Date(timestamp * 1000);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// 按自然日聚合（本地时区），返回按日期升序的数组，中间缺失的日期补零
export function groupByDay(logs) {
  if (logs.length === 0) return [];
  const map = new Map();
  let minTs = Infinity;
  let maxTs = -Infinity;
  for (const log of logs) {
    const ts = Number(log.created_at) || 0;
    if (ts < minTs) minTs = ts;
    if (ts > maxTs) maxTs = ts;
    const key = dayKey(ts);
    if (!map.has(key)) map.set(key, emptyTotals());
    accumulate(map.get(key), log);
  }
  // 从最早到最晚逐日填充
  const days = [];
  const cursor = new Date(minTs * 1000);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(maxTs * 1000);
  end.setHours(0, 0, 0, 0);
  // 防御：跨度超过 400 天时不再补零，直接返回有数据的日期
  const spanDays = Math.round((end - cursor) / 86400000) + 1;
  if (spanDays > 400) {
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, totals]) => finishDay(date, totals));
  }
  while (cursor <= end) {
    const key = dayKey(cursor.getTime() / 1000);
    const totals = map.get(key) || emptyTotals();
    days.push(finishDay(key, totals));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function finishDay(date, totals) {
  return { date, ...totals, usd: quotaToUSD(totals.quota) };
}

// 按模型聚合，按花费降序
export function groupByModel(logs) {
  const map = new Map();
  for (const log of logs) {
    const model = log.model_name || '(未知)';
    if (!map.has(model)) map.set(model, emptyTotals());
    accumulate(map.get(model), log);
  }
  const grandQuota = logs.reduce((s, l) => s + (Number(l.quota) || 0), 0);
  return Array.from(map.entries())
    .map(([model, totals]) => ({
      model,
      ...totals,
      usd: quotaToUSD(totals.quota),
      share: grandQuota > 0 ? totals.quota / grandQuota : 0,
      avgUseTime: totals.requests > 0 ? totals.useTimeSum / totals.requests : 0,
    }))
    .sort((a, b) => b.quota - a.quota);
}

// ---------- CSV 构建 ----------

// 明细 CSV：包含缓存读/写细分，花费为实际美元数值
export function buildDetailCSVRows(logs) {
  return logs.map((log) => {
    const cache = getCacheTokens(log);
    const prompt = Number(log.prompt_tokens) || 0;
    const completion = Number(log.completion_tokens) || 0;
    return {
      '时间': timestamp2string(log.created_at),
      '模型': log.model_name || '',
      '用时(秒)': log.use_time ?? '',
      '流式': log.is_stream ? '是' : '否',
      '提示tokens': prompt,
      '补全tokens': completion,
      '缓存读取tokens': cache.read,
      '缓存写入tokens': cache.writeAll,
      '缓存写入5m tokens': cache.write5m,
      '缓存写入1h tokens': cache.write1h,
      '花费': quotaToUSD(Number(log.quota) || 0).toFixed(6),
      '详情': log.content || '',
    };
  });
}

export function buildModelCSVRows(modelStats) {
  return modelStats.map((m) => ({
    '模型': m.model,
    '请求数': m.requests,
    '提示tokens': m.promptTokens,
    '补全tokens': m.completionTokens,
    '缓存读取tokens': m.cacheRead,
    '缓存写入tokens': m.cacheWrite,
    '总tokens': m.totalTokens,
    '花费': m.usd.toFixed(6),
    '花费占比(%)': (m.share * 100).toFixed(2),
    '平均用时(秒)': m.avgUseTime.toFixed(1),
  }));
}

export function buildDailyCSVRows(dayStats) {
  return dayStats.map((d) => ({
    '日期': d.date,
    '请求数': d.requests,
    '提示tokens': d.promptTokens,
    '补全tokens': d.completionTokens,
    '缓存读取tokens': d.cacheRead,
    '缓存写入tokens': d.cacheWrite,
    '总tokens': d.totalTokens,
    '花费': d.usd.toFixed(6),
  }));
}
